/**
 * The crosslisting side of the marketplace registry (ARCHITECTURE.md §6).
 * The `marketplaces` table holds every marketplace a sale can be recorded
 * against; this is the subset something can post to, and how: which worker
 * carries the job (the seller's browser extension, or a server-side API
 * connector), what each storefront accepts, and which job actions are built.
 *
 * Whether a marketplace is switched on in a country is data
 * (`marketplace_regions.crosslist_enabled`), so it can be turned off without a
 * deploy. What a worker can do is code, so it lives here, shared by the API
 * and the extension, which reports the actions it was built with.
 */
import { currencyExponent } from "@sable/domain";

export const JOB_ACTIONS = ["publish", "update_price", "delist"] as const;
export type JobAction = (typeof JOB_ACTIONS)[number];

export const CROSSLIST_MARKETPLACES = ["depop", "poshmark", "vinted", "ebay"] as const;
export type CrosslistMarketplace = (typeof CROSSLIST_MARKETPLACES)[number];

export type WorkerKind = "extension" | "api";

/**
 * Photo renditions per storefront, drawn by the worker just before upload
 * from the 2048 px master, so nothing extra is stored.
 *
 *   aspect  width / height of the output; null keeps the photo's own shape
 *   long    pixels on the long side
 *   max     the storefront's photo limit
 *
 * Depop and Poshmark show every photo square, so they get square renditions
 * letterboxed on `background` rather than the sites' own centre crop.
 */
export interface PhotoSpec {
  aspect: number | null;
  long: number;
  fit: "pad";
  background: string;
  max: number;
  quality: number;
}

export interface ConnectorSpec {
  code: CrosslistMarketplace;
  worker: WorkerKind;
  /** Null: the storefront has no title field (Depop shows the description's first line). */
  titleLimit: number | null;
  descriptionLimit: number;
  photoSpec: PhotoSpec;
  /**
   * The job actions a worker for this marketplace can carry out in this
   * build. An action that isn't here is never claimed; its jobs wait in the
   * queue until a worker that has it checks in.
   */
  actions: readonly JobAction[];
  /**
   * The worker reads this storefront's sold page on a schedule and sends it
   * in, so sales are recorded (and the item taken down elsewhere) without
   * anyone typing them in.
   */
  checksSales: boolean;
  /**
   * The worker can read the seller's whole shop on request, so the workspace's
   * listings are tied to its items and brought up to date ("Read my shops").
   */
  readsShop: boolean;
  /** Prices go up in whole units only (Poshmark: "Whole dollar amount required"). */
  wholePrices?: boolean;
  /** Storefront origin per country. A country that isn't here isn't supported. */
  sites: Readonly<Partial<Record<string, string>>>;
}

const square = (long: number, max: number): PhotoSpec => ({ aspect: 1, long, fit: "pad", background: "#ffffff", max, quality: 0.9 });
const natural = (long: number, max: number): PhotoSpec => ({ aspect: null, long, fit: "pad", background: "#ffffff", max, quality: 0.9 });

export const CONNECTORS: Readonly<Record<CrosslistMarketplace, ConnectorSpec>> = {
  depop: {
    code: "depop",
    worker: "extension",
    titleLimit: null,
    descriptionLimit: 1000,
    photoSpec: square(1280, 8),
    // Publishing and repricing read the seller's ship-from address and
    // location from their own session (docs/depop-posting.md). Switched on
    // 2026-10-02 on staging for the first supervised post.
    actions: ["publish", "update_price", "delist"],
    checksSales: true,
    readsShop: true,
    sites: { US: "https://www.depop.com" },
  },
  poshmark: {
    code: "poshmark",
    worker: "extension",
    titleLimit: 80,
    descriptionLimit: 1500,
    photoSpec: square(1200, 16),
    actions: ["publish", "update_price", "delist"],
    wholePrices: true,
    checksSales: true,
    readsShop: true,
    sites: { US: "https://poshmark.com" },
  },
  vinted: {
    code: "vinted",
    worker: "extension",
    titleLimit: 100,
    descriptionLimit: 2000,
    photoSpec: natural(1600, 20),
    actions: [],
    // The sold filter lives on the seller's own member page, whose id isn't known yet.
    checksSales: false,
    // The wardrobe is the seller's own member page, found from the signed-in session.
    readsShop: true,
    sites: { US: "https://www.vinted.com" },
  },
  ebay: {
    code: "ebay",
    worker: "api",
    titleLimit: 80,
    descriptionLimit: 4000,
    photoSpec: natural(1600, 24),
    // The Sell API connector (out-of-VPC, per-workspace OAuth) isn't built.
    actions: [],
    checksSales: false,
    readsShop: false,
    sites: { US: "https://www.ebay.com" },
  },
};

export function isCrosslistMarketplace(code: string): code is CrosslistMarketplace {
  return (CROSSLIST_MARKETPLACES as readonly string[]).includes(code);
}

export function connectorFor(code: string): ConnectorSpec | null {
  return isCrosslistMarketplace(code) ? CONNECTORS[code] : null;
}

/** The marketplaces a worker of this kind serves. */
export function marketplacesFor(worker: WorkerKind): CrosslistMarketplace[] {
  return CROSSLIST_MARKETPLACES.filter((m) => CONNECTORS[m].worker === worker);
}

/** Actions a worker can do: what it reports, limited to what this build knows. */
/**
 * The price a storefront takes, in minor units: rounded to the nearest whole
 * unit where it takes no cents (1250 → 1300 on Poshmark), else as given.
 */
export function storefrontPrice(code: string, priceMinor: number | null, currency: string): number | null {
  if (priceMinor === null || !connectorFor(code)?.wholePrices) return priceMinor;
  const unit = 10 ** currencyExponent(currency);
  return Math.round(priceMinor / unit) * unit;
}

export function effectiveActions(code: string, reported: readonly string[] | undefined): JobAction[] {
  const spec = connectorFor(code);
  if (!spec || !reported) return [];
  return spec.actions.filter((a) => reported.includes(a));
}
